import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import {
  Container,
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowForward,
  ArrowBack,
  CheckCircle
} from '@mui/icons-material';

// Import logo and illustrations
import healthIllustration from '../assets/images/health-illustration.svg';
import avenLogo from '../assets/images/aven-logo.svg';

const Register = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    agreeToTerms: false
  });
  
  // Form validation state
  const [formErrors, setFormErrors] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    agreeToTerms: ''
  });

  // Steps for the registration process
  const steps = ['Account Details', 'Personal Information', 'Payment', 'Success'];

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    const newValue = name === 'agreeToTerms' ? checked : value;
    
    setFormData({
      ...formData,
      [name]: newValue
    });
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  // Toggle password visibility
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Validate form fields
  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };
    
    // Validate based on current step
    if (activeStep === 0) {
      // Email validation
      if (!formData.email) {
        newErrors.email = 'Email is required';
        valid = false;
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email address is invalid';
        valid = false;
      }
      
      // Password validation
      if (!formData.password) {
        newErrors.password = 'Password is required';
        valid = false;
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
        valid = false;
      }
      
      // Confirm password validation
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
        valid = false;
      }
    } else if (activeStep === 1) {
      // Full name validation
      if (!formData.fullName) {
        newErrors.fullName = 'Full name is required';
        valid = false;
      }
      
      // Phone number validation (optional)
      if (formData.phoneNumber && !/^\+?[1-9]\d{9,14}$/.test(formData.phoneNumber)) {
        newErrors.phoneNumber = 'Please enter a valid phone number';
        valid = false;
      }
    } else if (activeStep === 2) {
      // Terms agreement validation
      if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = 'You must agree to the terms and conditions';
        valid = false;
      }
    }
    
    setFormErrors(newErrors);
    return valid;
  };

  // Handle next step
  const handleNext = () => {
    if (validateForm()) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Create the user data object according to the backend schema
      const userData = {
        email: formData.email,
        password: formData.password,
        full_name: formData.fullName,
        phone_number: formData.phoneNumber || ''
      };
      
      console.log('Sending registration data:', userData);
      
      // Submit registration data to API with proper headers
      const response = await axios.post(`${API_BASE_URL}/api/v1/users/register`, {
        user_in: userData,
        verification_id: null
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      console.log('Registration response:', response.data);
      
      // Store token in localStorage
      localStorage.setItem('token', response.data.access_token);
      
      // Show success message and continue to checkout
      setSuccess(true);
      navigate('/checkout');
    } catch (err) {
      console.error('Registration error:', err);
      // More detailed error handling
      if (err.response) {
        console.error('Error response:', err.response.data);
        setError(err.response.data.detail || 'Registration failed. Please check your information and try again.');
      } else if (err.request) {
        console.error('Error request:', err.request);
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('An unexpected error occurred. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Render step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <Typography variant="h5" gutterBottom>
              Create Your Account
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Enter your email and create a secure password
            </Typography>
            
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              variant="outlined"
              required
              autoFocus
            />
            
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              error={!!formErrors.password}
              helperText={formErrors.password}
              variant="outlined"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!formErrors.confirmPassword}
              helperText={formErrors.confirmPassword}
              variant="outlined"
              required
            />
            
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              fullWidth
              size="large"
              endIcon={<ArrowForward />}
              sx={{ mt: 2 }}
            >
              Continue
            </Button>
          </>
        );
      
      case 1:
        return (
          <>
            <Typography variant="h5" gutterBottom>
              Personal Information
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Tell us a bit about yourself
            </Typography>
            
            <TextField
              fullWidth
              label="Full Name"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              error={!!formErrors.fullName}
              helperText={formErrors.fullName}
              variant="outlined"
              required
            />
            
            <TextField
              fullWidth
              label="Phone Number (Optional)"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              error={!!formErrors.phoneNumber}
              helperText={formErrors.phoneNumber || "We'll use this for WhatsApp messaging if you opt in"}
              variant="outlined"
              placeholder="+1 (123) 456-7890"
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                startIcon={<ArrowBack />}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                endIcon={<ArrowForward />}
              >
                Continue
              </Button>
            </Box>
          </>
        );
      
      case 2:
        return (
          <>
            <Typography variant="h5" gutterBottom>
              Secure Payment
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Aven costs just $29 / month after a 7-day free trial.
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  color="primary"
                />
              }
              label="I agree to the Terms of Service and Privacy Policy"
            />
            {formErrors.agreeToTerms && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {formErrors.agreeToTerms}
              </Typography>
            )}

            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              endIcon={!loading && <ArrowForward />}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Complete Registration & Continue to Payment'}
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBack />} disabled={loading}>
                Back
              </Button>
            </Box>
          </>
        );
      case 3:
        return (
          <>
            <Typography variant="h5" gutterBottom>
              Review & Submit
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Please review your information and submit
            </Typography>
            
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Full Name
                  </Typography>
                  <Typography variant="body1">
                    {formData.fullName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {formData.email}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Phone Number
                  </Typography>
                  <Typography variant="body1">
                    {formData.phoneNumber || 'Not provided'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            
            <FormControlLabel
              control={
                <Checkbox
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <Link to="/terms" style={{ color: 'inherit', fontWeight: 'bold' }}>
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" style={{ color: 'inherit', fontWeight: 'bold' }}>
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />
            {formErrors.agreeToTerms && (
              <Typography variant="caption" color="error">
                {formErrors.agreeToTerms}
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                startIcon={<ArrowBack />}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading || !formData.agreeToTerms}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
              >
                {loading ? 'Registering...' : 'Complete Registration'}
              </Button>
            </Box>
          </>
        );
      
      default:
        return 'Unknown step';
    }
  };

  // If registration is successful, show success message
  if (success) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            mt: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
            Registration successful! Redirecting to dashboard...
          </Alert>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Grid container spacing={4}>
        {/* Left side - Form */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: { xs: 2, md: 4 },
            }}
          >
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
              <img src={avenLogo} alt="Aven Health Logo" height={60} />
            </Box>
            
            <Paper
              elevation={3}
              sx={{
                p: 4,
                width: '100%',
                borderRadius: 2,
              }}
            >
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}
              
              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              
              <form>
                {getStepContent(activeStep)}
              </form>
            </Paper>
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Already have an account?{' '}
                <Link to="/login" style={{ color: 'inherit', fontWeight: 'bold' }}>
                  Sign in
                </Link>
              </Typography>
            </Box>
          </Box>
        </Grid>
        
        {/* Right side - Illustration */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'primary.light',
            borderRadius: 2,
            p: 4,
            color: 'white',
          }}
        >
          <img
            src={healthIllustration}
            alt="Health Illustration"
            style={{ maxWidth: '100%', height: 'auto', marginBottom: '2rem' }}
          />
          
          <Typography variant="h4" gutterBottom>
            Your Health Journey Starts Here
          </Typography>
          
          <Typography variant="body1" paragraph>
            Join Aven Health and get personalized support for your GLP-1 medication journey.
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircle sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Medication reminders
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircle sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Track side effects
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircle sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    AI-powered coaching
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircle sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    WhatsApp integration
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Register;
